import React, { useState, useEffect, useRef } from 'react';
import { usePromoConfig } from '../hooks/usePromoConfig.ts';
import { useAnnouncements } from '../hooks/useAnnouncements.ts';
import { getGradientStyle } from '../config/announcementGradients.ts';
import { ChevronLeft, ChevronRight, X, Megaphone } from 'lucide-react';

interface PromoAnnouncementBannerProps {
  className?: string;
}

const PromoAnnouncementBanner: React.FC<PromoAnnouncementBannerProps> = ({ className = '' }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const autoScrollIntervalRef = useRef<number | null>(null);
  const userScrollTimeoutRef = useRef<number | null>(null);
  
  const { promoConfig } = usePromoConfig();
  const { announcements } = useAnnouncements();

  // Combine promo config and announcements
  const allAnnouncements = React.useMemo(() => {
    const items: Array<{ 
      id: string; 
      title: string; 
      message: string; 
      type: 'promo' | 'announcement';
      gradient_color?: string;
    }> = [];
    
    // Add promo config if active
    if (promoConfig?.is_active) {
      items.push({
        id: 'promo',
        title: promoConfig.promo_name,
        message: `🏆 ${promoConfig.promo_name} is LIVE! New users get R${promoConfig.starting_balance} to start. Deposits disabled. Trade to win a real cash prize! 🎯`,
        type: 'promo',
        gradient_color: 'default'
      });
    }
    
    // Add regular announcements
    announcements.forEach((announcement: { id: string; title: string; message: string; gradient_color?: string }) => {
      items.push({
        id: announcement.id,
        title: announcement.title,
        message: announcement.message,
        type: 'announcement',
        gradient_color: announcement.gradient_color || 'default'
      });
    });
    
    return items;
  }, [promoConfig, announcements]);

  // Auto-scroll through announcements
  useEffect(() => {
    if (allAnnouncements.length <= 1 || !isAutoScrolling || isUserScrolling) {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
      return;
    }

    // Store current length in a variable to avoid stale closure
    const announcementsLength = allAnnouncements.length;
    
    autoScrollIntervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % announcementsLength);
    }, 5000); // Change announcement every 5 seconds

    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
    };
  }, [allAnnouncements, isAutoScrolling, isUserScrolling]);

  // Handle manual navigation
  const handlePrevious = () => {
    setIsUserScrolling(true);
    setCurrentIndex((prev) => (prev - 1 + allAnnouncements.length) % allAnnouncements.length);
    setIsAutoScrolling(false);
    
    // Resume auto-scrolling after 10 seconds of no manual interaction
    if (userScrollTimeoutRef.current) {
      clearTimeout(userScrollTimeoutRef.current);
    }
    userScrollTimeoutRef.current = setTimeout(() => {
      setIsUserScrolling(false);
      setIsAutoScrolling(true);
    }, 10000);
  };

  const handleNext = () => {
    setIsUserScrolling(true);
    setCurrentIndex((prev) => (prev + 1) % allAnnouncements.length);
    setIsAutoScrolling(false);
    
    // Resume auto-scrolling after 10 seconds of no manual interaction
    if (userScrollTimeoutRef.current) {
      clearTimeout(userScrollTimeoutRef.current);
    }
    userScrollTimeoutRef.current = setTimeout(() => {
      setIsUserScrolling(false);
      setIsAutoScrolling(true);
    }, 10000);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current);
      }
    };
  }, []);

  // Handle transition when announcement changes
  useEffect(() => {
    if (allAnnouncements.length > 0) {
      setIsTransitioning(true);
      const timer = setTimeout(() => setIsTransitioning(false), 500);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, allAnnouncements.length]);

  // Ensure current index stays valid when announcements change
  useEffect(() => {
    if (allAnnouncements.length === 0) {
      return;
    }
    if (currentIndex >= allAnnouncements.length) {
      setCurrentIndex(0);
    }
  }, [allAnnouncements.length, currentIndex]);

  // Don't show if no announcements or dismissed
  if (allAnnouncements.length === 0 || !isVisible) return null;

  const currentAnnouncement = allAnnouncements[currentIndex];
  if (!currentAnnouncement) return null;
  const currentGradient = getGradientStyle(currentAnnouncement.gradient_color || 'default');

  return (
    <div 
      className={`text-white shadow-lg relative overflow-hidden transition-all duration-500 ease-in-out ${className}`}
      style={{ 
        background: currentGradient,
        opacity: isTransitioning ? 0.95 : 1
      }}
    >
      <div className="max-w-7xl mx-auto py-2 px-2 sm:px-6 md:py-4 lg:px-8">
        <div className="flex items-center justify-between flex-wrap gap-y-1">
          <div className="w-0 flex-1 flex items-center min-w-0">
            <span className="flex p-1.5 md:p-2 bg-white bg-opacity-20 rounded-lg flex-shrink-0">
              <Megaphone className="h-5 w-5 md:h-8 md:w-8 text-white" />
            </span>
            <div className="ml-2 md:ml-4 flex-1 min-w-0">
              <p className={`font-bold text-sm md:text-xl mb-0.5 md:mb-1 truncate transition-all duration-300 ${isTransitioning ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}>
                {currentAnnouncement.title}
              </p>
              <p className={`text-xs md:text-sm font-medium transition-all duration-300 delay-100 ${isTransitioning ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}>
                <span className="md:hidden truncate block">{currentAnnouncement.message}</span>
                <span className="hidden md:inline">{currentAnnouncement.message}</span>
              </p>
            </div>
          </div>
          
          {/* Navigation controls - only show if multiple announcements */}
          {allAnnouncements.length > 1 && (
            <div className="flex items-center gap-1 md:gap-2 mr-1 md:mr-2">
              <button
                type="button"
                onClick={handlePrevious}
                className="hidden md:block p-1 rounded-md hover:bg-white hover:bg-opacity-20 focus:outline-none focus:ring-2 focus:ring-white transition-all"
                aria-label="Previous announcement"
              >
                <ChevronLeft className="h-5 w-5 text-white" />
              </button>
              <div className="flex gap-0.5 md:gap-1">
                {allAnnouncements.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => {
                      setCurrentIndex(index);
                      setIsUserScrolling(true);
                      setIsAutoScrolling(false);
                      if (userScrollTimeoutRef.current) {
                        clearTimeout(userScrollTimeoutRef.current);
                      }
                      userScrollTimeoutRef.current = setTimeout(() => {
                        setIsUserScrolling(false);
                        setIsAutoScrolling(true);
                      }, 10000);
                    }}
                    className={`h-1.5 w-1.5 md:h-2 md:w-2 rounded-full transition-all ${
                      index === currentIndex 
                        ? 'bg-white' 
                        : 'bg-white bg-opacity-40 hover:bg-opacity-60'
                    }`}
                    aria-label={`Go to announcement ${index + 1}`}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={handleNext}
                className="hidden md:block p-1 rounded-md hover:bg-white hover:bg-opacity-20 focus:outline-none focus:ring-2 focus:ring-white transition-all"
                aria-label="Next announcement"
              >
                <ChevronRight className="h-5 w-5 text-white" />
              </button>
            </div>
          )}
          
          <div className="order-2 flex-shrink-0 sm:order-3 sm:ml-3">
            <button
              type="button"
              className="-mr-1 flex p-1.5 md:p-2 rounded-md hover:bg-white hover:bg-opacity-20 focus:outline-none focus:ring-2 focus:ring-white sm:-mr-2 transition-all"
              onClick={() => setIsVisible(false)}
            >
              <span className="sr-only">Dismiss</span>
              <X className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoAnnouncementBanner;

